import { Skeleton } from "@/components/ui/skeleton";
import { stripHtmlTags } from "@/utils/format";
import { Button, Icon, Badge, Heading } from "@elaniintc/goes-design-system";
import type { Correction, DeniedCorrection } from "@/queries/formality-request";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { FormCorrectionSectionList } from "./form-correction-list";
import { FormCorrectionGeneralComment } from "./general-comment";
import { useMemo } from "react";
import { useCorrectionForm } from "../context";

type VisibleStepsWithIndices = Array<{
  step: FormDetailResponse["steps"][number];
  stepNumber: number;
  visibleSections: FormDetailResponse["steps"][number]["sections"];
  sectionStartIndex: number;
  fieldStartIndex: number;
}>;

interface FormCorrectionReadOnlyViewProps {
  isFetchingRevisions: boolean;
  visibleStepsWithIndices: VisibleStepsWithIndices;
  isLoadingLeaderApproval: boolean;
  setLeaderModalState: (state: "idle" | "reject" | "approve") => void;
}

export const FormCorrectionReadOnlyView = ({
  isFetchingRevisions,
  isLoadingLeaderApproval,
  visibleStepsWithIndices,
  setLeaderModalState,
}: FormCorrectionReadOnlyViewProps) => {
  const {
    groupCorrectionInCurrentCycle,
    selectedRevision,
    form: data,
    formalityRequest,
    userProfile,
    isUserLeaderOfSelectedGroup,
    selectedGroupId,
  } = useCorrectionForm();
  // Determinar si mostrar "Enviado al líder"
  const showSentToLeaderBadge = useMemo(() => {
    if (!selectedRevision || !groupCorrectionInCurrentCycle) return false;
    return (
      selectedRevision.requires_leader_approval === true &&
      groupCorrectionInCurrentCycle.is_leader_approved === false
    );
  }, [selectedRevision, groupCorrectionInCurrentCycle]);

  const isUserLeaderOfCorrectionGroup = useMemo(() => {
    if (!userProfile?.data?.groups || !groupCorrectionInCurrentCycle)
      return false;

    const correctionGroupId =
      groupCorrectionInCurrentCycle.group?.id || selectedGroupId;

    if (!correctionGroupId) return false;

    const group = userProfile.data.groups.find(
      (g: { id: string; pivot?: { seniority: number } }) =>
        g.id === correctionGroupId,
    );
    return group?.pivot?.seniority === 1;
  }, [userProfile, groupCorrectionInCurrentCycle, selectedGroupId]);

  return (
    <div className="flex flex-col gap-8 px-4 pt-4">
      {/* Header del formulario */}
      <div className="flex justify-between items-center">
        <div>
          <Heading variant="h6">{data?.name}</Heading>
          <p className="font-body text-body text-size-body">
            {data?.description || "Revisa las respuestas del formulario."}
          </p>
        </div>
        <Badge variant="warning" className="h-5.5">
          {showSentToLeaderBadge ? "Pendiente de revisión" : "Observado"}
          <Icon type="fas" name="file-edit" className="text-warning-quiet" />
        </Badge>
      </div>

      {/* Información del analista que realizó la revisión (visible para el líder) */}
      {isUserLeaderOfSelectedGroup && groupCorrectionInCurrentCycle && (
        <div className="bg-neutral-object border border-neutral-quiet rounded-lg p-3 flex items-center gap-3">
          <Icon type="far" name="user" className="text-body text-base" />
          <div className="flex flex-col">
            <span className="text-body-loud text-sm font-semibold">
              {(() => {
                const creatorFromComments =
                  groupCorrectionInCurrentCycle?.comments?.[0]?.creator;
                const creator =
                  creatorFromComments || groupCorrectionInCurrentCycle?.creator;
                if (
                  creator &&
                  typeof creator === "object" &&
                  "name" in creator
                ) {
                  return `${creator.name || ""} ${creator.last_name || ""}`.trim();
                }
                return "Analista";
              })()}
            </span>
            {groupCorrectionInCurrentCycle?.group && (
              <span className="text-body text-xs">
                Grupo: {groupCorrectionInCurrentCycle.group?.name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Comentarios del líder (deniedCorrections) - visible solo para analistas, no para el líder */}
      {isFetchingRevisions ? (
        <Skeleton className="w-full h-16 rounded-lg" />
      ) : (
        !isUserLeaderOfSelectedGroup &&
        selectedRevision?.denied_corrections &&
        (selectedRevision?.denied_corrections?.length ?? 0) > 0 && (
          <div className="flex flex-col gap-2">
            {selectedRevision?.denied_corrections
              ?.filter((dc: DeniedCorrection) => {
                // Filtrar por grupo seleccionado si aplica
                if (selectedGroupId && dc.group?.id) {
                  return dc.group.id === selectedGroupId;
                }
                return true;
              })
              .filter((dc: DeniedCorrection) => {
                // No mostrar si el analista ya envió una corrección más reciente que el rechazo del líder
                const leaderComment =
                  dc.comments.length > 0 ? dc.comments.at(-1) : null;
                if (!leaderComment?.created_at) return true;
                const leaderRejectTime = new Date(
                  leaderComment.created_at,
                ).getTime();
                const groupId = dc.group?.id;
                // Verificar si hay una corrección posterior al rechazo del líder para el mismo grupo
                const hasNewerCorrection = (
                  selectedRevision.corrections ?? []
                ).some((c: Correction) => {
                  const corrGroupId = c.group?.id;
                  const matchesGroup = groupId ? corrGroupId === groupId : true;
                  const corrTime = new Date(c.created_at ?? 0).getTime();
                  return matchesGroup && corrTime > leaderRejectTime;
                });
                return !hasNewerCorrection;
              })
              .map((dc: DeniedCorrection) => {
                // El último mensaje de la corrección denegada siempre es del líder
                const leaderComment =
                  dc.comments.length > 0 ? dc.comments.at(-1) : null;
                if (!leaderComment?.text) return null;
                return (
                  <div
                    key={dc.id}
                    className="bg-surface-danger-accent-quiet border rounded-lg border-contextual-danger-quiet p-2 flex flex-col gap-1"
                  >
                    <div className="flex items-center gap-2">
                      <h1 className="text-h1 text-heading text-size-body-sm leading-2xl">
                        Comentario de visto bueno
                      </h1>
                      {leaderComment.creator &&
                        typeof leaderComment.creator === "object" &&
                        "name" in leaderComment.creator && (
                          <span className="text-body text-xs">
                            —{" "}
                            {(
                              leaderComment.creator as {
                                name?: string;
                                last_name?: string;
                              }
                            ).name || ""}{" "}
                            {(
                              leaderComment.creator as {
                                name?: string;
                                last_name?: string;
                              }
                            ).last_name || ""}
                          </span>
                        )}
                    </div>
                    <p className="font-body text-body text-size-body whitespace-pre-line text-justify wrap-break-word min-w-0">
                      {stripHtmlTags(leaderComment.text)}
                    </p>
                  </div>
                );
              })}
          </div>
        )
      )}

      {/* Comentario general si existe */}
      <FormCorrectionGeneralComment
        isFetching={isFetchingRevisions}
        correction={groupCorrectionInCurrentCycle}
      />

      {/* Formulario con campos en modo solo lectura */}
      <div className="flex flex-col gap-6">
        {visibleStepsWithIndices.map(
          ({
            step,
            stepNumber,
            visibleSections,
            sectionStartIndex,
            fieldStartIndex,
          }) => (
            <FormCorrectionSectionList
              key={step.id}
              formAssigment={
                data?.form_assignments?.[data?.form_assignments?.length - 1]
              }
              stepTitle={step.name}
              stepNumber={stepNumber}
              data={visibleSections}
              currentGroupId={selectedGroupId}
              isReadOnly={true}
              existingCorrection={groupCorrectionInCurrentCycle ?? undefined}
              sectionStartIndex={sectionStartIndex}
              fieldStartIndex={fieldStartIndex}
              applicant={{
                fullName: `${formalityRequest?.applicant?.first_name} ${formalityRequest?.applicant?.last_name}`,
                date:
                  formalityRequest?.updated_at || formalityRequest?.created_at,
              }}
            />
          ),
        )}
      </div>

      {/* Botones de líder: Rechazar y Aprobar */}
      {isUserLeaderOfCorrectionGroup && showSentToLeaderBadge && (
        <div className="flex gap-3 justify-end mt-4 sticky bottom-0 bg-surface-default py-4 border-t border-neutral-quiet">
          <Button
            variant="danger-secondary"
            onClick={() => setLeaderModalState("reject")}
            disabled={isLoadingLeaderApproval}
          >
            Rechazar
          </Button>
          <Button
            variant="default"
            onClick={() => setLeaderModalState("approve")}
            disabled={isLoadingLeaderApproval}
          >
            Aprobar
          </Button>
        </div>
      )}
    </div>
  );
};
